import { IonContent } from '@ionic/react';
import { Elements } from '@stripe/react-stripe-js';
import React from 'react';

import { CoinsPaymentSystemButton } from '@/components/CoinsPaymentSystemButton';
import { LoaderLine } from '@/components/LoaderLine';
import { StripeCoinsForm } from '@/components/StripeCoinsForm';
import { UiButtonBase } from '@/components/ui/ButtonBase';
import { STRIPE_PROMISE } from '@/constants/stripe';
import { useAppModals } from '@/hooks/useAppModals';
import { useColorScheme } from '@/hooks/useColorScheme';
import { useStripeV2Payment } from '@/hooks/useStripeV2Payment';
import { useStripeV2Plans } from '@/hooks/useStripeV2Plans';
import {
  Options,
  Option,
} from '@/pages/Paywall/sections/PaymentMethodSelection/components/Options';
import {
  ApplePayLabel,
  CreditCardLabel,
} from '@/pages/Paywall/sections/PaymentMethodSelection/components/PaySysLabel';
import { StripeV2CoinsPrice } from '@/services/stripe';
import { canUseApplePay } from '@/utils/canUseApplePay';

import { CoinsPaymentModalStyles } from './CoinsPayment.styles';
import { Description } from './components/Description';

export const CoinsPaymentModal: React.FC = () => {
  const { hide, isVisible, params } = useAppModals('buyCoinsPaywall');

  const [clientSecret, setClientSecret] = React.useState<string | null>(null);

  const { mutateAsync: createPayment, isLoading: isCreatingPayment } =
    useStripeV2Payment();

  React.useEffect(() => {
    if (!isVisible || !params?.product) {
      return;
    }

    const fetchClientSecret = async () => {
      const result = await createPayment({
        coins_price_id: params.product.id,
      });

      setClientSecret(result.secret);
    };
    fetchClientSecret();
  }, [setClientSecret, isVisible, createPayment, params?.product]);

  return (
    <CoinsPaymentModalStyles
      key="buy-coins-modal"
      className="coins-payment-modal"
      isOpen={isVisible}
      onDidDismiss={hide}
    >
      <IonContent>
        {params?.product && <Description product={params.product} />}

        {params && (
          <StripePaymentElements
            product={params.product}
            clientSecret={clientSecret}
            isCreatingPayment={isCreatingPayment}
          />
        )}
        <UiButtonBase
          onClick={hide}
          expand="full"
          variant="text"
          className="btn-buy-later"
        >
          Buy later
        </UiButtonBase>
      </IonContent>
    </CoinsPaymentModalStyles>
  );
};

function StripePaymentElements({
  clientSecret,
  isCreatingPayment,
  product,
}: {
  clientSecret: string | null;
  isCreatingPayment: boolean;
  product: StripeV2CoinsPrice;
}) {
  const { theme } = useColorScheme();
  const { hide } = useAppModals('upsale');

  const { coinsPlans } = useStripeV2Plans();

  const [paymentTabs, setPaymentTabs] = React.useState<
    ('applePay' | 'creditCard')[]
  >(['creditCard']);

  const [paymentMethod, setPaymentMethod] = React.useState<
    'applePay' | 'creditCard'
  >(() => {
    return 'creditCard';
  });

  React.useEffect(() => {
    if (canUseApplePay()) {
      setPaymentTabs(['applePay', 'creditCard']);
      setPaymentMethod('applePay');
    }
  }, []);

  if (isCreatingPayment || !clientSecret || !coinsPlans) {
    return <LoaderLine label="Loading payment elements..." />;
  }

  return (
    <>
      <Elements
        stripe={STRIPE_PROMISE}
        options={{
          clientSecret,
          appearance: {
            theme: theme === 'dark' ? 'night' : 'stripe',
            labels: 'above',
            variables: {
              borderRadius: '12px',
              colorDanger: '#eb445a',
            },
          },
        }}
        key={clientSecret}
      >
        <Options>
          {paymentTabs.includes('applePay') && (
            <Option
              selected={paymentMethod === 'applePay'}
              onClick={() => setPaymentMethod('applePay')}
              label="Apple Pay"
              labelIcon={<ApplePayLabel />}
            >
              <CoinsPaymentSystemButton
                product={product}
                disabled={isCreatingPayment}
                clientSecret={clientSecret}
                onCoinsIncrease={hide}
              />
            </Option>
          )}
          <Option
            selected={paymentMethod === 'creditCard'}
            onClick={() => setPaymentMethod('creditCard')}
            label="Credit card"
            labelIcon={<CreditCardLabel />}
          >
            <StripeCoinsForm
              clientSecret={clientSecret}
              product={product}
              disabled={isCreatingPayment}
              onCoinsIncrease={hide}
            />
          </Option>
        </Options>
      </Elements>
    </>
  );
}
